import { Banner, Button, ButtonGroup, LegacyStack, List, Text } from "@shopify/polaris";
import { useActiveOrganizationSelector } from "@vendor-app/app/_state/reducers/organizations";
import { parseShopName } from "@smartrr/shared/utils/parseShopName";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";

import { CustomerAccountsVersion } from "@smartrr/shared/shopifyGraphQL/api";

import { GettingStartedStoreAccess } from "../../../libs/store";

interface NewCustomerAccountsSetupProps {
  isAccountFileValid: boolean;
}

export function NewCustomerAccountsSetup({ isAccountFileValid }: NewCustomerAccountsSetupProps) {
  const org = useActiveOrganizationSelector();
  const { shopName } = parseShopName(org?.myShopifyDomain);
  const setup = GettingStartedStoreAccess.useSetup();
  const { update } = GettingStartedStoreAccess.useActions();

  const isNewCustomerAccounts = setup?.customerAccountsVersion === CustomerAccountsVersion.NewCustomerAccounts;

  const isStepCompleted = !!setup?.onSiteSetup.customerAccountPortalManualSetup?.completed;

  const onMarkAsDone = () => {
    update(
      [
        {
          path: "onSiteSetup.customerAccountPortalManualSetup",
          val: { completed: true, date: ISODateString.toString(new Date()) },
        },
      ],
      "customerPortal"
    );
  };

  const customerAccountContext = isNewCustomerAccounts || isAccountFileValid ? `Your store` : `This theme`;

  return (
    <LegacyStack vertical>
      <Banner status="info">
        <Text as="span" variant="bodyMd">
          <Text as="span" fontWeight="bold">
            {`${customerAccountContext} uses Shopify's new customer accounts.`}
          </Text>{" "}
          Follow the steps below to add a &quot;Subscriptions&quot; link that opens your Smartrr portal.
        </Text>
      </Banner>

      <List type="number">
        <List.Item>
          In Shopify admin, go to <b>Settings → Customer accounts</b> and click <b>Customize</b> next to your
          configuration. This takes you to the Checkout settings page.
        </List.Item>
        <List.Item>
          Under <b>Configurations</b>, click <b>Edit</b> to open the checkout/customer accounts editor.
        </List.Item>
        <List.Item>
          In the editor, click the page dropdown next to the page name at the top and select <b>Order status</b>{" "}
          under <b>Customer accounts</b>.
        </List.Item>
        <List.Item>
          In the left sidebar, click <b>Main</b>, then in the settings panel click the <b>Menu</b> field (it shows
          the current menu name) and choose <b>Edit</b>.
        </List.Item>
        <List.Item>
          In the panel that opens, click <b>Add menu item</b>, enter Label <b>Subscriptions</b> and Link{" "}
          <code>/a/subscriptions/customer-portal</code>, then <b>Save</b>.
        </List.Item>
      </List>

      <ButtonGroup>
        <Button disabled={isStepCompleted} onClick={onMarkAsDone}>
          Mark as done
        </Button>
        <Button primary external url={`https://admin.shopify.com/store/${shopName}/settings/customer_accounts`}>
          Open customer accounts editor
        </Button>
      </ButtonGroup>
    </LegacyStack>
  );
}
